import moment from "moment-timezone";

import CONSTANTS from "@/Constants.js";
import type {
  CustomFormAnswer,
  CustomFormSubmissionQuestion
} from "@/typings/customForm";
import { SYSTEM_TOKEN_LABELS } from "./customFormFields";

// How an answer reads. Lives outside the row component because search has to
// match the same text the row renders — two implementations would let a
// question be visible but unfindable, or found and then not shown.

// Types whose answer lives in selected_tokens rather than the scalar value.
const TOKEN_TYPES = [
  "status_ryg",
  "yes_no_na",
  "single_select",
  "multi_select"
];

// The answer's own field_type, not the question's live question_type. The server
// derives `value` from the snapshot taken when the answer was written, and a
// published template's question type can still be edited afterwards — reading
// the live type would reinterpret historical values (a stored "12 psi" rendering
// as "Yes" once the question became a checkbox). Falls back to the question only
// when there is no answer to have snapshotted anything.
export function answerFieldType(
  question: CustomFormSubmissionQuestion,
  answer: CustomFormAnswer | null
): string {
  return answer?.field_type ?? question.question_type;
}

export function isTokenType(fieldType: string): boolean {
  return TOKEN_TYPES.includes(fieldType);
}

// Tokens are stored, labels are displayed. An author's own option list wins; the
// system-owned map covers yes_no_na and status_ryg, whose vocabulary is fixed and
// whose options the builder does not persist as label pairs. An unrecognised
// token renders itself rather than vanishing.
export function tokenLabels(
  question: CustomFormSubmissionQuestion,
  answer: CustomFormAnswer,
  fieldType: string
): string[] {
  const options = question.options || [];
  const systemLabels = SYSTEM_TOKEN_LABELS[fieldType] || {};
  return (answer.selected_tokens || []).map(
    (token) =>
      options.find((option) => option.token === token)?.label ??
      systemLabels[token] ??
      token
  );
}

// The single line an answer reads as.
//
// Returns "" for signature and photo_only: those carry their answer as an image
// or a photo grid, not text, and the row renders them through their own branch.
// Callers that need every question to be findable — search — must fall back to
// the question's own text for those two.
export function answerDisplayValue(
  question: CustomFormSubmissionQuestion,
  answer: CustomFormAnswer | null
): string {
  if (!answer) return "";

  const type = answerFieldType(question, answer);
  if (isTokenType(type)) return tokenLabels(question, answer, type).join(", ");

  // A row can exist with no scalar value at all: WriteAnswer slices `note` and
  // `photo_ids` independently of `value`, so attaching a photo to a question
  // persists an answer whose value is null. Rendering that would invent an answer
  // the tech never gave — "No" for a checkbox, "Invalid date", "null of 5", or a
  // bare unit with no reading. Note 0 and false are values and must survive this.
  if (answer.value === null || answer.value === undefined) return "";

  switch (type) {
    case "checkbox":
      return answer.value ? "Yes" : "No";
    case "date":
      return formatDate(String(answer.value));
    case "star_rating":
      return `${answer.value} of 5`;
    case "number":
      // Not filter(Boolean): a reading of 0 is a real answer, and the server
      // agrees (0.present? makes it `answered`), so dropping it would hide the
      // value while the row still counted as satisfied.
      return [answer.value, question.unit]
        .filter((part) => part !== null && part !== undefined && part !== "")
        .join(" ");
    case "signature":
    case "photo_only":
      return "";
    default:
      return String(answer.value ?? "");
  }
}

export function formatDate(value: string): string {
  if (!value) return "";
  return moment(value).format(CONSTANTS.DATE_FORMAT);
}

export function formatDateTime(value: string): string {
  if (!value) return "";
  return moment(value).format(CONSTANTS.DATE_TIME_FORMAT);
}
